// Installed-Android camera permission preflight. This is not a media publisher:
// it only reads/requests the OS CAMERA permission through the local
// KataCameraPermission plugin so an explicit Preview can continue in the same
// action. WebView capture itself stays standard getUserMedia.

const capacitor = () => (typeof window === 'undefined' ? undefined : window.Capacitor);

export function isInstalledAndroid(cap = capacitor()) {
  try {
    return Boolean(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()
      && typeof cap.getPlatform === 'function' && cap.getPlatform() === 'android');
  } catch { return false; }
}

export const cameraPermissionPlugin = () => capacitor()?.Plugins?.KataCameraPermission || null;

// Capacitor PermissionState values; anything else is not authority.
const read = value => {
  const state = value?.camera;
  return ['granted', 'denied', 'prompt', 'prompt-with-rationale'].includes(state) ? state : null;
};

export async function readNativeCameraPermission({ plugin = cameraPermissionPlugin() } = {}) {
  try {
    if (typeof plugin?.checkPermissions !== 'function') return 'unavailable';
    return read(await plugin.checkPermissions()) || 'unavailable';
  } catch { return 'unavailable'; }
}

// Returns granted | denied | blocked | unavailable | stale. `isCurrent` fences the
// originating explicit action; a grant that outlives it is never consumed.
export async function ensureNativeCameraPermission({ plugin = cameraPermissionPlugin(), isCurrent }) {
  const state = await readNativeCameraPermission({ plugin });
  if (!isCurrent()) return 'stale';
  if (state === 'granted') return 'granted';
  // Capacitor reports `denied` only when Android will no longer show the prompt.
  if (state === 'denied') return 'blocked';
  if (state === 'unavailable' || typeof plugin.requestPermissions !== 'function') return 'unavailable';
  let result;
  try { result = read(await plugin.requestPermissions({ permissions: ['camera'] })); }
  catch { return isCurrent() ? 'unavailable' : 'stale'; }
  if (!isCurrent()) return 'stale';
  if (result === 'granted') return 'granted';
  return result ? 'denied' : 'unavailable';
}

export async function openNativeAppSettings({ plugin = cameraPermissionPlugin() } = {}) {
  try {
    if (typeof plugin?.openAppSettings !== 'function') return false;
    return (await plugin.openAppSettings())?.opened === true;
  } catch { return false; }
}
